import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

interface Props {
  onReveal?: () => void;
  /** Fraction of overlay that must be cleared before the rest fades. */
  revealAt?: number;
}

/**
 * Interactive scratch card (ported from the prototype, prototype §5.8).
 *
 * Renders a reward image (Amazon gift card) underneath a green canvas
 * overlay. The user "scratches" the overlay with mouse or touch — scratched
 * pixels are cleared via `globalCompositeOperation = 'destination-out'`.
 * Once `revealAt` fraction of the overlay is cleared, the whole overlay
 * fades to show the reward in full.
 *
 * Image loading is done with a preload Image() so we know definitively
 * whether `/Amazon.png` is present before deciding whether to show the
 * fallback placeholder — `onError` on a JSX <img> can fire before React
 * attaches the handler.
 */
export function ScratchCard({ onReveal, revealAt = 0.35 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [imgOk, setImgOk] = useState<boolean | null>(null);

  // Preload the gift card image. naturalWidth === 0 means the file didn't
  // load even if onload fires (some servers return HTML 404s).
  useEffect(() => {
    const img = new window.Image();
    img.onload = () => setImgOk(img.naturalWidth > 0);
    img.onerror = () => setImgOk(false);
    img.src = "/Amazon.png";
  }, []);

  /** Paint the green overlay using CSS-pixel coordinates. */
  const paintOverlay = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = wrap.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    if (w === 0 || h === 0) return;

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0); // reset any prior scale
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    ctx.scale(dpr, dpr);
    ctx.globalCompositeOperation = "source-over";

    // Brand-700 solid fill
    ctx.fillStyle = "#0e5a37";
    ctx.fillRect(0, 0, w, h);

    // Subtle diagonal sheen — gives the surface a tactile feel
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, "rgba(255,255,255,0.10)");
    grad.addColorStop(0.5, "rgba(255,255,255,0)");
    grad.addColorStop(1, "rgba(0,0,0,0.18)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Hint text — no emoji, just the label
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.font = `600 ${Math.round(w * 0.05)}px "Inter", system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Scratch here", w / 2, h / 2);
    ctx.restore();
  }, []);

  /** Set canvas pixel buffer to match the wrapper, then repaint. */
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const rect = wrap.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    paintOverlay();
  }, [paintOverlay]);

  // useLayoutEffect ensures the canvas is sized + painted before the
  // first paint cycle, so users never see an empty/transparent canvas.
  useLayoutEffect(() => {
    resizeCanvas();
    const ro = new ResizeObserver(() => resizeCanvas());
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [resizeCanvas]);

  const pointFromEvent = (e: React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const scratchSegment = useCallback(
    (x0: number, y0: number, x1: number, y1: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0); // strokes in buffer pixels
      ctx.globalCompositeOperation = "destination-out";
      ctx.strokeStyle = "rgba(0,0,0,1)";
      // Big brush — about 22% of the card height — so each stroke clears
      // a generous swath of the overlay.
      ctx.lineWidth = Math.max(80, canvas.height * 0.22);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
      ctx.restore();
    },
    [],
  );

  const computeCleared = useCallback((): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const ctx = canvas.getContext("2d");
    if (!ctx) return 0;
    const w = canvas.width;
    const h = canvas.height;
    if (w === 0 || h === 0) return 0;
    const step = 8;
    const data = ctx.getImageData(0, 0, w, h).data;
    let cleared = 0;
    let total = 0;
    for (let y = 0; y < h; y += step) {
      for (let x = 0; x < w; x += step) {
        const idx = (y * w + x) * 4 + 3;
        if ((data[idx] ?? 255) < 32) cleared++;
        total++;
      }
    }
    return total === 0 ? 0 : cleared / total;
  }, []);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (revealed) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    const p = pointFromEvent(e);
    lastPointRef.current = p;
    scratchSegment(p.x, p.y, p.x + 0.1, p.y + 0.1);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!drawingRef.current || revealed) return;
    const p = pointFromEvent(e);
    const last = lastPointRef.current ?? p;
    scratchSegment(last.x, last.y, p.x, p.y);
    lastPointRef.current = p;
  };

  const finishStroke = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    if (revealed) return;
    const cleared = computeCleared();
    if (cleared >= revealAt) {
      setRevealed(true);
      onReveal?.();
    }
  }, [computeCleared, onReveal, revealAt, revealed]);

  return (
    <div
      ref={wrapRef}
      className="relative w-[min(82vw,420px)] aspect-[3/2] rounded-2xl overflow-hidden shadow-card select-none"
    >
      {/* Reward layer — always render SOMETHING (placeholder by default).
          Swap to the real /Amazon.png only when we've confirmed it loads. */}
      <div className="absolute inset-0">
        {imgOk ? (
          <img
            src="/Amazon.png"
            alt="Your reward"
            className="w-full h-full object-cover block"
            draggable={false}
          />
        ) : (
          <GiftCardPlaceholder />
        )}
      </div>

      {/* Scratch overlay — z-10 so it's unambiguously above the reward layer. */}
      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishStroke}
        onPointerCancel={finishStroke}
        onPointerLeave={finishStroke}
        className={`absolute inset-0 w-full h-full z-10 touch-none transition-opacity duration-700 ${
          revealed
            ? "opacity-0 pointer-events-none"
            : "opacity-100 cursor-grab active:cursor-grabbing"
        }`}
      />
    </div>
  );
}

/** Visual stand-in for `/Amazon.png` when the file isn't present. */
function GiftCardPlaceholder() {
  return (
    <div className="w-full h-full relative bg-neutral-900 text-white flex flex-col items-center justify-center font-sans overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(ellipse at 80% 30%, #1f1f1f 0%, transparent 55%), radial-gradient(ellipse at 20% 80%, #1f1f1f 0%, transparent 55%)",
        }}
      />
      <div className="relative text-center px-6">
        <div className="text-[14px] font-semibold tracking-wide uppercase mb-1 opacity-80">
          Gift card
        </div>
        <div className="text-[64px] leading-none font-bold tracking-tight mb-1">
          $25
        </div>
        <div className="text-[11px] uppercase tracking-[0.25em] opacity-70 mt-2">
          Reward
        </div>
      </div>
    </div>
  );
}
